import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useStytchB2BClient, useStytchMember } from '@stytch/react/b2b';
import { ApiError, describeInviteLink, redeemInviteLink, type InviteLinkPreview } from '../api';
import { getFreshBrowserSessionJwt } from '../auth/teamJwt';
import { PanelState, Wordmark } from '../components/Chrome';
import { consoleConfig } from '../config';
import { ALERT_ERROR, BUTTON_PRIMARY_LG, BUTTON_TEXT, CALLBACK_SCREEN, CARD, EYEBROW, FIELD_LABEL } from '../controlStyles';
import { orgRootPath } from '../routing';
import { clearPendingJoin } from './pendingJoin';

type Phase =
  | { status: 'loading' }
  | { status: 'ready'; preview: InviteLinkPreview }
  | { status: 'failed'; message: string };

/** The server's refusals are already sentences; anything else is an outage. */
function describeJoinFailure(cause: unknown): string {
  if (cause instanceof ApiError && cause.status === 429) return 'Too many attempts. Try again in an hour.';
  if (cause instanceof ApiError && cause.status >= 400 && cause.status < 500) return cause.message;
  return 'The Nimbalyst service could not be reached. Try again in a moment.';
}

/**
 * `/join/<token>`: a shareable team invite link. Mounted behind sign-in, which
 * returns here; the server joins the signed-in email, with the link's role.
 */
export function JoinPage() {
  const stytch = useStytchB2BClient();
  const { member } = useStytchMember();
  const navigate = useNavigate();
  const { token = '' } = useParams();
  const [phase, setPhase] = useState<Phase>({ status: 'loading' });
  const [busy, setBusy] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  useEffect(() => {
    // This tab is now handling the link; a later landing on `/` must not come back here.
    clearPendingJoin();
    let cancelled = false;
    getFreshBrowserSessionJwt(stytch.session, consoleConfig.sessionDurationMinutes)
      .then((jwt) => describeInviteLink(jwt, token))
      .then((preview) => { if (!cancelled) setPhase({ status: 'ready', preview }); })
      .catch((cause: unknown) => {
        if (cancelled) return;
        console.error('[join] invite link could not be read', cause);
        setPhase({ status: 'failed', message: describeJoinFailure(cause) });
      });
    return () => { cancelled = true; };
  }, [stytch, token]);

  const join = async () => {
    setBusy(true);
    setJoinError(null);
    try {
      const jwt = await getFreshBrowserSessionJwt(stytch.session, consoleConfig.sessionDurationMinutes);
      const joined = await redeemInviteLink(jwt, token);
      // The workspace loads the team list when it mounts, so the joined org is in it.
      navigate(orgRootPath({ orgId: joined.orgId }));
    } catch (cause) {
      console.error('[join] invite link was not redeemed', cause);
      setJoinError(describeJoinFailure(cause));
      setBusy(false);
    }
  };

  let body;
  if (phase.status === 'loading') {
    body = <PanelState>Reading the invite link…</PanelState>;
  } else if (phase.status === 'failed') {
    body = (
      <PanelState tone="error" className="join-failed" role="alert">
        <strong>This invite link cannot be used</strong>
        <span>{phase.message} Ask a team admin for a new one.</span>
      </PanelState>
    );
  } else {
    const { preview } = phase;
    const team = preview.teamName?.trim() || 'this team';
    body = (
      <section className={`join-invite ${CARD} grid gap-5 text-left`} aria-labelledby="join-heading">
        <div className="grid gap-2">
          <span className={EYEBROW}>Team invite</span>
          <h1 id="join-heading" className="m-0 text-[1.45rem] font-semibold leading-tight text-nim">
            {preview.alreadyMember ? `You are already in ${team}` : `Join ${team}`}
          </h1>
          {!preview.alreadyMember && (
            <p className="m-0 text-[0.88rem] leading-relaxed text-nim-muted">
              You will join as <span className="capitalize">{preview.role}</span>. You can use the team in the browser; nothing needs to be installed.
            </p>
          )}
        </div>
        <div className="grid gap-1">
          <span className={FIELD_LABEL}>Signed in as</span>
          <span className="text-[0.92rem] text-nim">{member?.email_address ?? ''}</span>
        </div>
        {joinError && <div className={ALERT_ERROR} role="alert">{joinError}</div>}
        <div className="flex flex-wrap items-center gap-3">
          {preview.alreadyMember ? (
            <Link className={`${BUTTON_PRIMARY_LG} no-underline`} to={orgRootPath({ orgId: preview.orgId })}>Open team</Link>
          ) : (
            <button className={BUTTON_PRIMARY_LG} type="button" disabled={busy} onClick={() => void join()}>
              {busy ? 'Joining…' : 'Join team'}
            </button>
          )}
          <Link className={`${BUTTON_TEXT} no-underline`} to="/">Not now</Link>
        </div>
      </section>
    );
  }

  return (
    <main className={CALLBACK_SCREEN}>
      <div className="join-frame grid w-[min(30rem,100%)] gap-6">
        <div className="justify-self-center"><Wordmark /></div>
        {body}
      </div>
    </main>
  );
}
